import { MatchStatus, MatchUnitSize } from '@uefadigital/panenka';
import { PkMatchUnit } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import type { MouseEvent } from 'react';
import { useEffect, useMemo, memo, useRef } from 'react';

import { withErrorBoundary } from '../ErrorBoundary';
import { useWindowSize } from '../hooks/useWindowSize';
import type { BroadcastersParams } from '../queries/broadcasters';

import Broadcasters from './Broadcasters';
import Highlight from './Highlight';
import MatchJsonLd from './MatchJsonLd';
import { useCtaAriaLabel } from './useCtaAriaLabel';
import useMatch from './useMatch';
import useMatchBroadcasters from './useMatchBroadcasters';
import useMatchHighlight from './useMatchHighlight';
import { useMatchTracking } from './useMatchTracking';
import { useMatchUnitTranslations } from './useMatchUnitTranslations';

import { trackCardElement } from '@services/GoogleTagManagerService';
import { getMatchUrl } from '@services/LinkRuleService';
import type { Match } from '@shared-types/match.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { language as defaultLanguage } from '@utilities/browser';
import { matchLink, isPlaceholderMatch } from '@utilities/live';
import { getMatchTracking } from '@utilities/match-tracking';

const teamServiceUrl = `/api/v1/linkrules/team/{{teamId}}/?competitionId={{competitionId}}&phase=${window.currentPhase}`;

export type MatchUnitProps = {
  id?: string;
  options: {
    broadcasters?: BroadcastersParams;
    showHighlights?: boolean;
    disableTeamsLink?: boolean;
    disableScorerLink?: boolean;
    hideDate?: boolean;
    hideRound?: boolean;
    hideContext?: boolean;
    showSeason?: boolean;
    showRedCards?: boolean;
    hideSeeMore?: boolean;
    useTeamCode?: boolean;
    disableHighLightIconClick?: boolean;
  };
  className?: string;
  match: Match;
  onUpdate?: (match: Match) => unknown;
  disableMatchLink?: boolean;
  size?: MatchUnitSize;
  language?: string;
  disableTeamsLink?: boolean;
  disableScorerLink?: boolean;
  hideDate?: boolean;
  hideRound?: boolean;
  hideContext?: boolean;
  showSeason?: boolean;
  showRedCards?: boolean;
  hideSeeMore?: boolean;
  useTeamCode?: boolean;
  disableTracking?: boolean;
  onClick?: (e: MouseEvent) => void;
  shownInBrackets?: boolean;
};

export const MatchUnit = ({
  id,
  options,
  match: originalMatch,
  onUpdate,
  size,
  disableMatchLink,
  className,
  language = defaultLanguage,
  disableTeamsLink = options.disableTeamsLink,
  disableScorerLink = options.disableScorerLink,
  showSeason = options.showSeason,
  showRedCards = options.showRedCards,
  hideDate = options.hideDate,
  hideRound = options.hideRound,
  hideContext = options.hideContext,
  useTeamCode = options.useTeamCode,
  hideSeeMore = options.hideSeeMore,
  disableTracking,
  onClick,
  shownInBrackets,
}: MatchUnitProps) => {
  const { isMobile } = useWindowSize();
  const muSize = size === MatchUnitSize.XL && isMobile ? MatchUnitSize.M : size;
  const horizontal = [MatchUnitSize.M, MatchUnitSize.XL].includes(muSize);
  const vertical = [MatchUnitSize.S, MatchUnitSize.L].includes(muSize);
  const isPlusSize = [MatchUnitSize.S_PLUS, MatchUnitSize.L_PLUS].includes(muSize);

  showRedCards ??= isPlusSize;
  const match = useMatch(originalMatch, showRedCards);
  const ctaAriaLabel = useCtaAriaLabel(match);

  const { translations, dateCalendarFormat } = useMatchUnitTranslations();

  const isDomestic = match.competition?.region === 'DOMESTIC';
  const isPlaceholder = isPlaceholderMatch(match);

  const prevMatch = useRef(match);
  useEffect(() => {
    if (!onUpdate || !match?.hash || match.hash === prevMatch.current.hash) return;
    prevMatch.current = match;

    onUpdate(match);
  }, [match, onUpdate]);

  const highlightsVideo = useMatchHighlight(
    match,
    !isDomestic && options.showHighlights && match.status === MatchStatus.FINISHED,
  );

  const broadcasters = useMatchBroadcasters(match, options, horizontal);

  const tracking = useMatchTracking(match, options, disableTracking);
  const ctaTracking = useMemo(() => getMatchTracking(match, null, 'see more'), [match]);

  const href = !disableMatchLink && !isDomestic && !isPlaceholder ? matchLink(getMatchUrl(match.id)) : null;
  const Tag = href ? 'a' : 'div';

  const hideCta = hideSeeMore || !href;

  const trackMuClick = (e: MouseEvent) => {
    onClick?.(e);

    if (!tracking || e.button > 1) return;
    GoogleTagManagerHelper.gtmCardElementClick(e.target as HTMLElement, tracking);
  };

  const trackCtaClick = (e: CustomEvent) => {
    if (hideCta || !ctaTracking) return;
    GoogleTagManagerHelper.gtmCardElementClick(e.target as HTMLElement, ctaTracking);
  };

  if (!match?.id) return null;

  return (
    <Tag
      id={id || undefined}
      ref={trackCardElement(tracking, true)}
      href={href}
      className={clsx({
        mu: true,
        'mu-horizontal': horizontal,
        [`mu-horizontal--${muSize}`.toLowerCase()]: horizontal,
        'mu-vertical': vertical,
        [`mu-vertical--${muSize}`.toLowerCase()]: vertical,
        [className]: !!className,
      })}
      data-status={match.status}
      onClick={trackMuClick}
      onAuxClick={trackMuClick}
      title={ctaAriaLabel}
    >
      {!!match.kickOffTime?.dateTime && <MatchJsonLd match={originalMatch} />}
      <PkMatchUnit
        className='pk-match-unit'
        matchData={match}
        language={language?.toUpperCase() as any}
        teamServiceUrl={teamServiceUrl}
        disableTeamsLink={disableTeamsLink}
        disableScorerLink={disableScorerLink}
        size={muSize}
        onPkCtaClick={trackCtaClick}
        translations={translations}
        dateCalendarFormat={dateCalendarFormat}
        showSeason={showSeason}
        showRedCards={showRedCards}
        hideDate={hideDate}
        hideRound={hideRound}
        hideContext={hideContext}
        hideCta={hideCta}
        useTeamCode={useTeamCode}
        showHighlightUrl={!!highlightsVideo}
        ctaAriaLabel={ctaAriaLabel}
        shownInBrackets={shownInBrackets}
      >
        {horizontal && <Broadcasters broadcasters={broadcasters} slot='cta' />}

        {!!highlightsVideo && (
          <Highlight
            match={match}
            size={muSize}
            video={highlightsVideo}
            disableHighLightIconClick={options.disableHighLightIconClick}
          />
        )}
      </PkMatchUnit>
    </Tag>
  );
};

export default withErrorBoundary(memo(MatchUnit));
